/**
 * Interest-related debug field definitions
 *
 * ## Data Source Priority
 *
 * Fields prefer `bondAllocations` (calculated at capture time) over `cashFlow.bondPayments`
 * (pre-distribution values). The fallback to cashFlow exists for:
 * - Post-distribution captures (after applyDistributionPlan)
 * - Legacy code paths that don't provide bondAllocations
 *
 * During normal debug capture from the revenue calculator, bondAllocations is always available.
 */

import type { DebugFieldDefinition } from '../types';
import { maxBondField, sumBondField } from './extraction-helpers';
import { formatCurrency, formatPercent } from './formatters';

export const interestFields: DebugFieldDefinition[] = [
  {
    id: 'interest.obligation',
    name: 'Interest Obligation',
    category: 'interest',
    description: 'Total interest obligation at this payment (cascaded from later payments)',
    extractor: (ctx) => {
      // Prefer decisionContext (accurate value at calculation time)
      if (ctx.decisionContext?.currentInterestObligation !== undefined) {
        return ctx.decisionContext.currentInterestObligation;
      }
      // Fallback to cashFlow (only accurate after applyDistributionPlan)
      return ctx.cashFlow?.interestObligation;
    },
    formatter: formatCurrency,
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'interest.totalNewInterest',
    name: 'Total New Interest',
    category: 'interest',
    description: 'Sum of new interest generated across all bonds',
    extractor: (ctx) => sumBondField(ctx, 'newInterest'),
    formatter: formatCurrency,
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'interest.totalFutureObligation',
    name: 'Total Future Interest Obligation',
    category: 'interest',
    description: 'Sum of future interest obligations across all bonds',
    extractor: (ctx) => sumBondField(ctx, 'futureInterestObligation'),
    formatter: formatCurrency,
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'interest.total',
    name: 'Total Interest',
    category: 'interest',
    description: 'Sum of total interest (new + future) across all bonds',
    extractor: (ctx) => sumBondField(ctx, 'totalInterest'),
    formatter: formatCurrency,
    formula: (ctx) => {
      const newInterest = sumBondField(ctx, 'newInterest') ?? 0;
      const futureObligation = sumBondField(ctx, 'futureInterestObligation') ?? 0;
      const total = sumBondField(ctx, 'totalInterest') ?? 0;
      return `New Interest ($${newInterest.toLocaleString()}) + Future Obligation ($${futureObligation.toLocaleString()}) = $${total.toLocaleString()}`;
    },
    performanceCost: 'low',
    enabledByDefault: true,
  },
  {
    id: 'interest.maxRate',
    name: 'Maximum Interest Rate',
    category: 'interest',
    description: 'Highest interest rate among active bonds at this payment',
    extractor: (ctx) => maxBondField(ctx, 'interestRate'),
    formatter: formatPercent,
    performanceCost: 'low',
    enabledByDefault: false,
  },
];
